import DOMPurify from "dompurify";

type InlineImage = { contentId?: string; inlineUrl?: string };

/** Email code never runs. Only the parent reader measures this script-free document. */
export function mailDocument(html: string, attachments: InlineImage[]) {
  const sanitized = DOMPurify.sanitize(html, {
    WHOLE_DOCUMENT: true,
    RETURN_DOM: true,
    ADD_TAGS: ["link"],
    FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "input", "button", "textarea", "select", "base", "meta"],
    FORBID_ATTR: ["autofocus", "formaction", "ping", "autoplay"],
  }) as HTMLElement;
  let missingInlineImages = 0;
  for (const image of sanitized.querySelectorAll("img")) {
    const source = image.getAttribute("src") ?? "";
    let reason = "";
    if (/^cid:/i.test(source)) {
      let cid = source.slice(4);
      try { cid = decodeURIComponent(cid); } catch { /* Keep malformed IDs literal. */ }
      const attachment = attachments.find(item => item.inlineUrl && item.contentId?.replace(/^<|>$/g, "") === cid.replace(/^<|>$/g, ""));
      if (attachment?.inlineUrl) image.setAttribute("src", attachment.inlineUrl);
      else { missingInlineImages++; reason = "内嵌图片未找到"; }
    }
    if (source.startsWith("//")) image.setAttribute("src", "https:" + source);
    image.setAttribute("loading", "eager");
    image.setAttribute("decoding", "async");
    image.setAttribute("referrerpolicy", "no-referrer");
    image.style.setProperty("max-width", "100%", "important");
    image.style.setProperty("height", "auto", "important");
    const pixel = image.hasAttribute("width") && image.hasAttribute("height") && (Number(image.getAttribute("width")) <= 1 || Number(image.getAttribute("height")) <= 1);
    if (pixel) {
      image.style.setProperty("width", Math.max(0, Number(image.getAttribute("width"))) + "px", "important");
      image.style.setProperty("height", Math.max(0, Number(image.getAttribute("height"))) + "px", "important");
    } else if (!image.getAttribute("alt")) image.setAttribute("alt", "邮件图片");
    if (reason) {
      const placeholder = document.createElement("span");
      placeholder.className = "mail-image-placeholder";
      placeholder.textContent = image.getAttribute("alt")?.trim() || reason;
      placeholder.setAttribute("title", reason);
      image.replaceWith(placeholder);
    }
  }
  for (const link of sanitized.querySelectorAll("link")) {
    if (link.getAttribute("rel")?.toLowerCase() !== "stylesheet" || !/^(https:)?\/\//i.test(link.getAttribute("href") ?? "")) link.remove();
  }
  for (const media of sanitized.querySelectorAll("video,audio")) {
    media.setAttribute("controls", ""); media.setAttribute("preload", "metadata");
  }
  for (const anchor of sanitized.querySelectorAll("a")) {
    anchor.setAttribute("target", "_blank"); anchor.setAttribute("rel", "noopener noreferrer");
  }
  const styles = [...sanitized.querySelectorAll("head style,head link")].map(style => style.outerHTML).join("");
  const body = sanitized.querySelector("body");
  const declaredLanguage = body?.getAttribute("lang") || sanitized.getAttribute("lang") || "";
  const language = /^[a-z]{2,3}(?:-[a-z0-9]{2,8})*$/i.test(declaredLanguage) ? declaredLanguage : "";
  // Keep the sender's body colors/classes and responsive rules, inside our measured wrapper.
  const content = document.createElement("div");
  if (body) {
    for (const name of ["style", "class", "dir", "lang", "bgcolor"]) {
      const value = body.getAttribute(name); if (value) content.setAttribute(name, value);
    }
    if (body.getAttribute("bgcolor")) content.style.backgroundColor = body.getAttribute("bgcolor")!;
    for (const child of Array.from(body.childNodes)) content.appendChild(child);
  }
  content.classList.add("mail-document");
  const css = `
    html{color-scheme:light;background:white}
    body{font:15px/1.7 -apple-system,BlinkMacSystemFont,"Segoe UI","Microsoft YaHei",sans-serif;color:#1d1d1f;margin:0!important;padding:16px!important;min-height:0!important;height:auto!important;box-sizing:border-box;overflow-wrap:anywhere}
    .mail-document{display:flow-root;max-width:100%;min-width:0!important;height:auto!important}
    img,video,svg{max-width:100%!important;height:auto!important}audio{max-width:100%}table{max-width:100%}pre{white-space:pre-wrap}a{color:#087f65}
    .mail-image-placeholder{display:inline-block;max-width:100%;padding:2px 6px;border-radius:6px;background:#eef3f0;color:#52645a;font:12px/1.6 system-ui,sans-serif;overflow-wrap:anywhere}
    @media(max-width:600px){
      body{padding:12px!important}
      .mail-document,.mail-document *{min-width:0!important;max-width:100%!important;box-sizing:border-box!important}
      .mail-document table{width:100%!important;table-layout:fixed!important}
      .mail-document td,.mail-document th,.mail-document col{width:auto!important;overflow-wrap:anywhere!important;word-break:normal!important}
      .mail-document p,.mail-document a,.mail-document td,.mail-document th,.mail-document [nowrap]{white-space:normal!important}
    }
  `;
  return {
    missingInlineImages,
    language,
    document: '<!doctype html><html' + (language ? ' lang="' + language + '"' : '') + '><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta name="referrer" content="no-referrer"><meta http-equiv="Content-Security-Policy" content="default-src \'none\'; script-src \'none\'; img-src https: http: data: blob:; style-src \'unsafe-inline\' https:; font-src https: data:; media-src https: data: blob:; base-uri \'none\'; form-action \'none\'">' + styles + '<style>' + css + '</style></head><body>' + content.outerHTML + '</body></html>',
  };
}
